import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { usePreviewBoardVisibility } from '../common/usePreviewBoardVisibility';
import {
  HighlightChessboard,
  ThemeProvider,
  trainerLinePlyIndex,
} from 'react-chess-core';
import { fenLineFromUciMoves, parsePreviewMovesUci } from './reviewCardPreview';
import { nonInteractivePreviewBoardShellSx } from '../common/previewBoardShell';
import { useUserSettings } from '../settings/useUserSettings';

const MOVE_MS = 650;
const LOOP_PAUSE_MS = 900;
const MOVE_ANIMATION_MS = 220;

type ReviewCardPreviewBoardProps = {
  startFen?: string;
  staticFen?: string;
  movesUci?: string;
  boardOrientation?: 'white' | 'black';
  boardWidth?: number;
  animate?: boolean;
  /** Cycle through preview plies. Set false for static browse tiles on touch. */
  autoplay?: boolean;
  startDelayMs?: number;
};

const ReviewCardPreviewBoard = ({
  startFen,
  staticFen,
  movesUci,
  boardOrientation = 'white',
  boardWidth = 144,
  animate = true,
  autoplay = true,
  startDelayMs = 0,
}: ReviewCardPreviewBoardProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { animationsActive } = usePreviewBoardVisibility(containerRef);
  const { settings } = useUserSettings();
  const parsedMovesUci = useMemo(() => parsePreviewMovesUci(movesUci), [movesUci]);
  const fens = useMemo(
    () =>
      animate
        ? fenLineFromUciMoves(startFen, movesUci)
        : staticFen
          ? [staticFen]
          : fenLineFromUciMoves(startFen, movesUci, 0),
    [animate, movesUci, startFen, staticFen],
  );
  const initialPly = trainerLinePlyIndex(parsedMovesUci);
  const [ply, setPly] = useState(initialPly);
  const [animationDuration, setAnimationDuration] = useState(MOVE_ANIMATION_MS);

  useEffect(() => {
    setPly(trainerLinePlyIndex(parsedMovesUci));
    setAnimationDuration(MOVE_ANIMATION_MS);
  }, [fens, parsedMovesUci]);

  useEffect(() => {
    if (!autoplay || !animate || !animationsActive || fens.length <= 1) {
      return;
    }

    let cancelled = false;
    let plyLocal = 0;
    let timeoutId = 0;

    const step = () => {
      if (cancelled) {
        return;
      }

      const next = (plyLocal + 1) % fens.length;
      if (next === 0) {
        setAnimationDuration(0);
        window.requestAnimationFrame(() => {
          if (!cancelled) {
            setAnimationDuration(MOVE_ANIMATION_MS);
          }
        });
      }

      plyLocal = next;
      setPly(plyLocal);

      const delay = next === 0 ? LOOP_PAUSE_MS : MOVE_MS;
      timeoutId = window.setTimeout(step, delay);
    };

    timeoutId = window.setTimeout(step, startDelayMs + MOVE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [animate, animationsActive, autoplay, fens, startDelayMs]);

  const displayPly = autoplay ? ply : initialPly;
  const fen = fens[displayPly] ?? staticFen;
  const lineContext =
    parsedMovesUci.length > 0
      ? { movesUci: parsedMovesUci, plyIndex: displayPly }
      : undefined;

  if (!fen) {
    return (
      <Box
        aria-hidden
        sx={{
          ...nonInteractivePreviewBoardShellSx(boardWidth),
          bgcolor: 'action.hover',
        }}
      />
    );
  }

  return (
    <Box ref={containerRef} sx={nonInteractivePreviewBoardShellSx(boardWidth)}>
      <ThemeProvider theme="dark" boardTheme={settings.boardTheme}>
        <HighlightChessboard
          position={fen}
          boardWidth={boardWidth}
          boardOrientation={boardOrientation}
          arePiecesDraggable={false}
          showBoardNotation={false}
          animationDuration={
            animate && autoplay && animationsActive ? animationDuration : 0
          }
          checkSquare=""
          hintSquare={null}
          incorrectMoveSquare={null}
          lineContext={lineContext}
        />
      </ThemeProvider>
    </Box>
  );
};

export default ReviewCardPreviewBoard;
